import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ImagePicker from '@/components/ImagePicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Star, User } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/imageUrl';
import { API_URL } from '@/config/api';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [courseTypes, setCourseTypes] = useState([]);
  const [formData, setFormData] = useState({
    student_name: '',
    instructor_name: '',
    content: '',
    rating: 5,
    image_url: '',
    course_type: '',
    featured: false,
  });

  useEffect(() => {
    fetchTestimonials();
    fetchCourseTypes();
  }, []);

  const fetchCourseTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories?type=course_type`);
      setCourseTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch course types:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTestimonial) {
        await axios.put(`${API_URL}/testimonials/${editingTestimonial.id}`, formData);
        toast.success('更新成功');
      } else {
        await axios.post(`${API_URL}/testimonials`, formData);
        toast.success('新增成功');
      }
      setDialogOpen(false);
      resetForm();
      fetchTestimonials();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/testimonials/${deleteTarget}`);
      toast.success('刪除成功');
      fetchTestimonials();
    } catch (error) {
      toast.error('刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const openDialog = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        student_name: testimonial.student_name,
        instructor_name: testimonial.instructor_name,
        content: testimonial.content,
        rating: testimonial.rating,
        image_url: testimonial.image_url,
        course_type: testimonial.course_type,
        featured: testimonial.featured,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTestimonial(null);
    setFormData({
      student_name: '',
      instructor_name: '',
      content: '',
      rating: 5,
      image_url: '',
      course_type: '',
      featured: false,
    });
  };

  // Merge managed types with current value (so legacy values display correctly when editing)
  const courseTypeOptions = (() => {
    const list = courseTypes.map((c) => c.name);
    if (formData.course_type && !list.includes(formData.course_type)) {
      return [...list, formData.course_type];
    }
    return list;
  })();

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="text-3xl font-bold">學員分享管理</h1>
            <p className="text-muted-foreground">管理學員成功分享、照片和評價</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()} data-testid="add-testimonial-button">
                <Plus className="mr-2 h-4 w-4" /> 新增心得
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTestimonial ? '編輯心得' : '新增心得'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>學員姓名 *</Label>
                    <Input
                      value={formData.student_name}
                      onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>導師姓名 *</Label>
                    <Input
                      value={formData.instructor_name}
                      onChange={(e) => setFormData({ ...formData, instructor_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>心得內容 *</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={6}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>評分 (1-5)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      step="0.5"
                      value={formData.rating}
                      onChange={(e) => setFormData({ ...formData, rating: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>課程類別</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.course_type}
                      onChange={(e) => setFormData({ ...formData, course_type: e.target.value })}
                    >
                      <option value="">請選擇課程類別...</option>
                      {courseTypeOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <ImagePicker
                  label="學員照片"
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  />
                  <Label htmlFor="featured">首頁精選（勾選後顯示在首頁「學員成功分享」區域）</Label>
                </div>
                <div className="flex gap-2">
                  <Button type="submit">儲存</Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    取消
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">載入中...</div>
        ) : testimonials.length > 0 ? (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full min-w-[480px]" style={{ tableLayout: 'fixed' }}>
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left p-3 overflow-hidden" style={{ width: '160px', resize: 'horizontal' }}>學員</th>
                  <th className="text-left p-3 overflow-hidden hidden md:table-cell" style={{ width: '120px', resize: 'horizontal' }}>導師</th>
                  <th className="text-left p-3 overflow-hidden hidden lg:table-cell" style={{ resize: 'horizontal' }}>內容</th>
                  <th className="text-left p-3 overflow-hidden hidden md:table-cell" style={{ width: '100px', resize: 'horizontal' }}>評分</th>
                  <th className="text-left p-3 overflow-hidden" style={{ width: '70px' }}>精選</th>
                  <th className="text-right p-3 overflow-hidden" style={{ width: '100px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.map((t) => {
                  const imgSrc = resolveImageUrl(t.image_url);
                  return (
                    <tr key={t.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {imgSrc ? (
                            <img src={imgSrc} alt={t.student_name} className="w-9 h-9 rounded-full object-cover border-2 border-blue-100 flex-shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-blue-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold text-sm truncate">{t.student_name}</div>
                            <div className="text-xs text-muted-foreground truncate">{t.course_type}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 hidden md:table-cell text-sm truncate" title={t.instructor_name}>{t.instructor_name}</td>
                      <td className="p-3 hidden lg:table-cell text-sm text-muted-foreground truncate" title={t.content}>
                        {t.content.substring(0, 60)}...
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${i < Math.floor(t.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="p-3">
                        {t.featured ? (
                          <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded font-medium">精選</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => openDialog(t)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(t.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            暫無學員心得，請點擊「新增心得」開始添加。
          </div>
        )}
      </div>
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此心得？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，心得將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminTestimonials;
